import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { LoginErrorDialog } from "@/components/layout/login-error-dialog";
import { useLoginForm } from "@/hooks/use-login-form";

// Inner form component so the useLoginForm state (incl. the typed password) is discarded
// wholesale every time the dialog opens — the parent remounts it via key below.
function CredentialsForm({ prefilledLoginId, onSuccess, onCancel }) {
  const form = useLoginForm({
    initialLoginId: prefilledLoginId != null ? prefilledLoginId : undefined,
    onSuccess,
  });

  return (
    <>
      <div className="space-y-3">
        <Input
          type="text"
          value={form.loginId}
          onChange={(event) => form.setLoginId(event.target.value)}
          placeholder="email"
          disabled={form.isSubmitting}
          autoFocus={!form.loginId}
          className="h-11"
        />
        <Input
          type="password"
          value={form.password}
          onChange={(event) => form.setPassword(event.target.value)}
          onKeyDown={(event) => {
            if (event.key === "Enter") {
              event.preventDefault();
              void form.submit();
            }
          }}
          placeholder="password"
          disabled={form.isSubmitting}
          autoFocus={Boolean(form.loginId)}
          className="h-11"
        />
      </div>
      <DialogFooter>
        <Button type="button" variant="outline" onClick={onCancel} disabled={form.isSubmitting}>
          Cancel
        </Button>
        <Button type="button" onClick={() => void form.submit()} disabled={form.isSubmitting}>
          {form.isSubmitting ? "Signing in…" : "Sign in"}
        </Button>
      </DialogFooter>
      <LoginErrorDialog
        open={form.errorDialog.open}
        kind={form.errorDialog.kind}
        onOpenChange={(open) => form.setErrorDialog((prev) => ({ ...prev, open }))}
      />
    </>
  );
}

/**
 * Full-credentials sign-in as a dialog: serves "Add account" and switches to profiles without
 * a PIN (prefilled loginId). Same submit flow as the lock-screen LoginPanel via useLoginForm.
 * onSuccess(result, { password }) — the session is already applied; the password rides along
 * in memory so the caller can offer PIN setup without retyping.
 */
export function CredentialsDialog({ open, onOpenChange, prefilledLoginId, onSuccess }) {
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-sm">
        <DialogHeader>
          <DialogTitle>Sign in</DialogTitle>
          <DialogDescription>
            {prefilledLoginId
              ? "Enter the password for this account to switch to it."
              : "Use Clarimed credentials to add this account on this device."}
          </DialogDescription>
        </DialogHeader>
        <CredentialsForm
          key={`${open ? "open" : "closed"}:${prefilledLoginId || ""}`}
          prefilledLoginId={prefilledLoginId}
          onSuccess={(result, extras) => {
            onOpenChange?.(false);
            onSuccess?.(result, extras);
          }}
          onCancel={() => onOpenChange?.(false)}
        />
      </DialogContent>
    </Dialog>
  );
}
